/**
 * The ONE place the frontend decides what a role may do.
 *
 * This mirrors `ROLE_HIERARCHY` in `server/src/routes/admin/user_management.py`
 * exactly, and it exists because the two drifted. `AdminDashboard` defined its
 * own `isSuperAdmin` as `role === 'super_admin' || role === 'owner'`, so a shop
 * OWNER was shown the Etsy Quota, Shop Reset and Agents tabs — Shop Reset being
 * the destructive one — while the backend's `has_role_access(role, 'super_admin')`
 * correctly refused them. The UI offered controls the API would reject.
 *
 * A local `role === 'x' || role === 'y'` comparison is what caused that. Use
 * `hasRoleAccess` instead: it is ordered, so a new role slots in one place.
 */

/** Ascending privilege. Index IS the level — mirrors the backend list. */
export const ROLE_HIERARCHY = ['member', 'admin', 'owner', 'super_admin'];

/** Numeric level for a role; an unknown role is the lowest, never the highest. */
export function getRoleLevel(role) {
  const i = ROLE_HIERARCHY.indexOf(role);
  return i === -1 ? 0 : i;
}

/**
 * True when `role` is at least `required` in the hierarchy.
 *
 * The two arguments fail in OPPOSITE directions, on purpose. An unknown SUBJECT
 * (`role`) is the lowest level, so a bogus role grants nothing. An unknown
 * `required` cannot go through `getRoleLevel` at all: it would come back 0, the
 * comparison would read `>= 0`, and EVERY caller would pass — a typo in a
 * `requiredRole` prop would silently delete the gate rather than break it. So an
 * unrecognised `required` denies everyone; the gate fails loudly-closed instead.
 */
export function hasRoleAccess(role, required) {
  if (!ROLE_HIERARCHY.includes(required)) {
    return false;
  }
  return getRoleLevel(role) >= getRoleLevel(required);
}

/**
 * The Administration section — the sidebar group and the /admin dashboard it
 * links to — is super_admin ONLY. Deliberately not `hasRoleAccess(role, 'admin')`:
 * every tab behind it is a platform-operations tool (system overview, user
 * management, shop reset, design remap, quota), none of which is a seller
 * concern, and the backend routers under /admin/* are gated the same way.
 */
export const ADMINISTRATION_ROLE = 'super_admin';

export function canAccessAdministration(role) {
  return hasRoleAccess(role, ADMINISTRATION_ROLE);
}
